// Client-side data layer. Everything the old Flask + SQLite backend used to
// persist now lives in the browser's localStorage instead, so the demo runs
// as a static site with zero server. Shapes intentionally mirror the old
// SQLite rows so the existing UI components don't need to change.

const DETECTIONS_KEY = 'fsqa_demo_detections_v1';
const SCHEDULES_KEY = 'fsqa_demo_schedules_v1';
const INTEGRATIONS_KEY = 'fsqa_demo_integrations_v1';
const EMAIL_SETTINGS_KEY = 'fsqa_demo_email_settings_v1';

// The Detection Log is meant to last only for the current page load, not
// forever. This module is only re-evaluated on a real browser refresh (a
// full reload re-runs the whole JS bundle) - client-side navigation between
// Dashboard/Analytics/History does NOT reload this module, so the log still
// behaves normally while actually using the app. Refreshing gives a clean
// slate every time, on purpose.
try {
  window.localStorage.removeItem(DETECTIONS_KEY);
} catch {
  // ignore storage errors
}

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage full or unavailable - demo continues with in-memory state only
  }
}

export function todayKey() {
  return new Date().toLocaleDateString('en-GB').replace(/\//g, '-');
}

function parseDMY(key) {
  const [d, m, y] = (key || '').split('-').map(Number);
  if (!d || !m || !y) return 0;
  return new Date(y, m - 1, d).getTime();
}

let idCounter = null;
function nextDetectionId() {
  if (idCounter === null) {
    const rows = readJson(DETECTIONS_KEY, []);
    idCounter = rows.reduce((max, r) => Math.max(max, r.id || 0), 0);
  }
  idCounter += 1;
  return idCounter;
}

// ---- Detections -----------------------------------------------------------

export function getAllDetections() {
  return readJson(DETECTIONS_KEY, []);
}

export function getDetections({ date, camera = 'all', limit = 100 } = {}) {
  let rows = getAllDetections();
  if (date && date !== 'all') rows = rows.filter((r) => r.date === date);
  if (camera && camera !== 'all') rows = rows.filter((r) => r.camera === camera);
  rows = [...rows].sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
  return rows.slice(0, limit);
}

export function addDetection(row) {
  const rows = getAllDetections();
  const id = nextDetectionId();
  const full = { id, resolved: false, occurrences: 1, created_at: Date.now(), ...row };
  rows.unshift(full);
  writeJson(DETECTIONS_KEY, rows);
  return full;
}

export function updateDetection(id, patch) {
  const rows = getAllDetections();
  const idx = rows.findIndex((r) => r.id === id);
  if (idx === -1) return null;
  rows[idx] = { ...rows[idx], ...patch };
  writeJson(DETECTIONS_KEY, rows);
  return rows[idx];
}

export function resolveDetectionRow(id) {
  return updateDetection(id, { resolved: true, resolved_at: new Date().toLocaleTimeString('en-GB') });
}

export function clearDate(date) {
  const rows = getAllDetections().filter((r) => r.date !== date);
  writeJson(DETECTIONS_KEY, rows);
}

// Wipes every detection regardless of date - used by the Dashboard's "Clear
// session log" button, which is meant to reset the whole running session
// (every camera/date/clip the user has run so far), not just whichever
// archive date happens to be selected at that moment.
export function clearAllDetections() {
  writeJson(DETECTIONS_KEY, []);
}

export function getAvailableDates() {
  const rows = getAllDetections();
  const dates = new Set(rows.map((r) => r.date));
  dates.add(todayKey());
  return [...dates].sort((a, b) => parseDMY(b) - parseDMY(a));
}

function summarizeRows(rows) {
  const byClass = {};
  const byCamera = {};
  rows.forEach((r) => {
    byClass[r.class_name] = (byClass[r.class_name] || 0) + 1;
    byCamera[r.camera] = (byCamera[r.camera] || 0) + 1;
  });
  return {
    total_alerts: rows.length,
    by_class: Object.entries(byClass)
      .map(([class_name, count]) => ({ class_name, count }))
      .sort((a, b) => b.count - a.count),
    by_camera: Object.entries(byCamera)
      .map(([camera, count]) => ({ camera, count }))
      .sort((a, b) => b.count - a.count),
  };
}

export function getSummary(date) {
  const rows = getDetections({ date, limit: 100000 });
  return { date, ...summarizeRows(rows) };
}

// Same shape as getSummary(), but across every date the current session has
// touched - what the Dashboard's live view uses so switching camera/date/clip
// never makes the numbers (or the log/feed backing them) drop to zero.
export function getSessionSummary() {
  return { date: 'session', ...summarizeRows(getAllDetections()) };
}

// ---- Email schedules --------------------------------------------------------

export function getSchedules() {
  return readJson(SCHEDULES_KEY, []);
}

function saveSchedules(list) {
  writeJson(SCHEDULES_KEY, list);
}

export function addSchedule(payload) {
  const list = getSchedules();
  const id = list.reduce((max, s) => Math.max(max, s.id || 0), 0) + 1;
  const row = { id, enabled: true, last_sent_at: null, ...payload };
  list.push(row);
  saveSchedules(list);
  return row;
}

export function updateScheduleRow(id, payload) {
  const list = getSchedules();
  const idx = list.findIndex((s) => s.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], ...payload };
  saveSchedules(list);
  return list[idx];
}

export function deleteScheduleRow(id) {
  saveSchedules(getSchedules().filter((s) => s.id !== id));
}

export function getScheduleRow(id) {
  return getSchedules().find((s) => s.id === id) || null;
}

export function touchScheduleSent(id) {
  return updateScheduleRow(id, { last_sent_at: new Date().toISOString() });
}

// ---- Email settings ---------------------------------------------------------

const DEFAULT_EMAIL_SETTINGS = {
  recipient: '', frequency: 'manual', send_time: '09:00', weekday: 'monday', enabled: false, last_sent_at: null,
};

export function getEmailSettings() {
  return { ...DEFAULT_EMAIL_SETTINGS, ...readJson(EMAIL_SETTINGS_KEY, {}) };
}

export function saveEmailSettingsRow(payload) {
  const merged = { ...getEmailSettings(), ...payload };
  writeJson(EMAIL_SETTINGS_KEY, merged);
  return merged;
}

export function touchEmailSent() {
  return saveEmailSettingsRow({ last_sent_at: new Date().toISOString() });
}

// ---- Integrations ------------------------------------------------------------

export function getIntegrations() {
  return readJson(INTEGRATIONS_KEY, []);
}

export function saveIntegrationRow(payload) {
  const list = getIntegrations();
  if (payload.id) {
    const idx = list.findIndex((i) => i.id === payload.id);
    if (idx !== -1) {
      list[idx] = { ...list[idx], ...payload };
      writeJson(INTEGRATIONS_KEY, list);
      return list[idx];
    }
  }
  const id = list.reduce((max, i) => Math.max(max, i.id || 0), 0) + 1;
  const row = { id, last_tested_at: null, ...payload };
  list.push(row);
  writeJson(INTEGRATIONS_KEY, list);
  return row;
}

export function getIntegrationRow(id) {
  return getIntegrations().find((i) => i.id === id) || null;
}

export function deleteIntegrationRow(id) {
  writeJson(INTEGRATIONS_KEY, getIntegrations().filter((i) => i.id !== id));
}

export function touchIntegrationTested(id) {
  const list = getIntegrations();
  const idx = list.findIndex((i) => i.id === id);
  if (idx === -1) return null;
  list[idx] = { ...list[idx], last_tested_at: new Date().toISOString() };
  writeJson(INTEGRATIONS_KEY, list);
  return list[idx];
}
